
import Vue from "vue";
import { db } from "@/utils/dexie";
import moment from "moment";
import eventBus from "@/eventBus";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { exportCsv } from "@/utils/csv";
import { APP_TITLE } from "@/constants/app";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";

export default Vue.extend({
  name: "ResultList",
  props: {
    calendarValue: Object as () => moment.Moment,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data() {
    return {
      dialog: false,
      search: "",
      results: [] as any[],
      isLoading: false,
    };
  },
  computed: {
    filteredResults(): any[] {
      if (!this.search) return this.results;
      const s = this.search.toLowerCase();
      return this.results.filter((item) => {
        return (
          item.date.includes(s) ||
          item.payment_composition_amount.toString().includes(s) ||
          item.refund_amount.toString().includes(s)
        );
      });
    },
    totalOrderCount(): number {
      return this.filteredResults.reduce(
        (sum, item) => sum + (item.order_count || 0),
        0
      );
    },
    totalPaymentCompositionAmount(): number {
      return this.filteredResults.reduce(
        (sum, item) => sum + (item.payment_composition_amount || 0),
        0
      );
    },
    totalRefundCount(): number {
      return this.filteredResults.reduce(
        (sum, item) => sum + (item.refund_count || 0),
        0
      );
    },
    totalRefundAmount(): number {
      return this.filteredResults.reduce(
        (sum, item) => sum + (item.refund_amount || 0),
        0
      );
    },
  },
  watch: {
    calendarValue: {
      handler() {
        if (this.dialog) {
          this.render();
        }
      },
      immediate: false,
    },
    orderPathFilter: {
      handler() {
        if (this.dialog) this.render();
      },
      deep: true,
    },
  },
  methods: {
    open() {
      this.dialog = true;
      this.render();
    },
    close() {
      this.dialog = false;
      this.$emit("close");
    },
    closeSilently() {
      this.dialog = false;
    },
    select(item: any) {
      eventBus.$emit("query", item.date, item.date);
    },
    exportCsv(): void {
      if (!this.results || this.results.length === 0) return;
      const headers = [
        "결제일자",
        "결제건수",
        "결제 합계",
        "환불건수",
        "환불금액",
        "예상 순정산액",
      ];
      const rows = this.results.map((item: any) => {
        const netPayout =
          (item.payment_composition_amount || 0) + (item.refund_amount || 0);
        return [
          item.date,
          item.order_count || 0,
          item.payment_composition_amount || 0,
          item.refund_count || 0,
          item.refund_amount || 0,
          netPayout,
        ];
      });

      exportCsv(
        headers,
        rows,
        `${APP_TITLE}_월별정산목록_${moment().format("YYYYMMDD_HHmmss")}.csv`
      );
    },
    async render() {
      if (!this.calendarValue) return;
      const mallInfo = this.$store.state.mall_info;
      if (!mallInfo) {
        this.$set(this, "results", []);
        return;
      }
      this.isLoading = true;
      try {
        const start_date = this.calendarValue
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end_date = this.calendarValue
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        const dates = getDatesFromStartEnd(start_date, end_date);

        const results = [] as any[];
        for (const date of dates) {
          const row = await db.results
            .where("[mall_id+shop_no+date]")
            .equals([mallInfo.mall_id, mallInfo.shop_no, date])
            .first();

          if (!row) {
            continue;
          }
          results.push(filterSettlementResult(row, this.orderPathFilter));
        }

        this.$set(this, "results", results);
      } catch (e) {
        console.error("ResultList render error:", e);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
